import { memo, useCallback, useState } from 'react';
import { StatusIcon } from './StatusIcon';
import { Button } from '~/components/ui/Button';
import { Dropdown } from '~/components/ui/Dropdown';
import type { DomainEntry } from '~/lib/stores/domains';

const GENERAL_TEXT_CLASS_NAME = 'text-xs text-bolt-ds-textSecondary';

const DOMAIN_DESCRIPTIONS = {
  primary: 'Primary domain',
  alias: 'Domain alias',
  redirect: 'Redirect to primary domain',
} as const;

const STATUS_MESSAGES = {
  pending: 'Pending resolution (up to 24h)',
  'ssl-pending': 'Pending SSL certificate',
} as const;

interface DomainEntryItemProps {
  domainEntry: DomainEntry;
  onResolveDNSIssue: () => void;
  onRemove: () => void;
  onSetPrimary: () => void;
}

export const DomainEntryItem = memo(
  ({ domainEntry, onResolveDNSIssue, onRemove, onSetPrimary }: DomainEntryItemProps) => {
    const [isOptionsOpen, setOptionsOpen] = useState(false);

    const handleResolveDNSIssue = useCallback(() => {
      onResolveDNSIssue();
    }, [onResolveDNSIssue]);

    const handleClickPrimary = useCallback(() => {
      onSetPrimary();
    }, [onSetPrimary]);

    const onRemoveDomain = useCallback(() => {
      onRemove();
    }, [onRemove]);

    return (
      <div className="px-4 py-3 bg-bolt-ds-surfaceTwo rounded-md border border-bolt-ds-borderDivider">
        <div className="flex items-start gap-3">
          <StatusIcon status={domainEntry.status} />
          <div className="flex-1 gap-1">
            <p className="font-medium text-sm text-bolt-ds-textPrimary">{domainEntry.domain}</p>
            <p className="text-xs text-bolt-ds-textSecondary">{DOMAIN_DESCRIPTIONS[domainEntry.type]}</p>
          </div>

          <div className="flex items-center gap-2 self-center">
            {domainEntry.status === 'not-configured' && (
              <Button variant="secondary" size="sm" className="text-xs" onClick={handleResolveDNSIssue}>
                Resolve DNS issues
              </Button>
            )}
            {domainEntry.status === 'pending' && <p className={GENERAL_TEXT_CLASS_NAME}>{STATUS_MESSAGES.pending}</p>}
            {domainEntry.status === 'ssl-pending' && (
              <p className={GENERAL_TEXT_CLASS_NAME}>{STATUS_MESSAGES['ssl-pending']}</p>
            )}

            <Dropdown
              trigger={
                <button className="text-bolt-ds-textSecondary hover:text-bolt-ds-textPrimary i-heroicons:ellipsis-horizontal size-4"></button>
              }
              open={isOptionsOpen}
              onOpenChange={setOptionsOpen}
              contentClassName="w-auto"
            >
              {domainEntry.type !== 'primary' && (
                <Dropdown.Item className="text-sm text-left" onClick={handleClickPrimary}>
                  Set as primary domain
                </Dropdown.Item>
              )}
              {domainEntry.type !== 'redirect' && (
                <Dropdown.Item className="text-sm text-left" onClick={onRemoveDomain}>
                  Remove domain
                </Dropdown.Item>
              )}
            </Dropdown>
          </div>
        </div>
      </div>
    );
  },
);

DomainEntryItem.displayName = 'DomainEntryItem';
